/* eslint-disable no-nested-ternary */
import { useState, useEffect, useCallback } from 'react';
import { Box, Grid, Card, Typography, Skeleton, useTheme } from '@mui/material';
import { motion } from 'framer-motion';
import CountUp from 'react-countup';
import PeopleAltRounded from '@mui/icons-material/PeopleAltRounded';
import PersonSearchRounded from '@mui/icons-material/PersonSearchRounded';
import TrackChangesRounded from '@mui/icons-material/TrackChangesRounded';
import PaidRounded from '@mui/icons-material/PaidRounded';
import CampaignRounded from '@mui/icons-material/CampaignRounded';
import BorderColorRounded from '@mui/icons-material/BorderColorRounded';
import { dashboardApi } from '../../../api/dashboardApi';
import useMounted from '../../../hooks/useMounted';

// "91945.8" -> "91,9 k€ en pipeline" ; "950" -> "950 € en pipeline"
const formatPipeline = (value) => {
  const n = Number(value) || 0;
  if (n >= 1000) {
    const k = (n / 1000).toLocaleString('fr-FR', { minimumFractionDigits: 1, maximumFractionDigits: 1 });
    return `${k} k€ en pipeline`;
  }
  return `${n.toLocaleString('fr-FR')} € en pipeline`;
};

const KpiCard = ({ loading, label, value, color, icon, subtitle, isCurrency }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  return (
    <Card
      component={motion.div}
      whileHover={{ y: -4 }}
      sx={{
        p: 2.5,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        borderRadius: '16px',
        backgroundColor: isDark ? 'rgba(30, 41, 59, 0.6)' : '#ffffff',
        border: `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)'}`,
        boxShadow: isDark
          ? '0 4px 20px -2px rgba(0,0,0,0.3)'
          : '0 4px 20px -2px rgba(0,0,0,0.04)',
        transition: 'box-shadow 0.3s ease, border-color 0.3s ease',
        '&:hover': {
          boxShadow: isDark
            ? `0 12px 26px -6px rgba(0,0,0,0.5), 0 0 14px ${color}33`
            : `0 12px 26px -6px rgba(0,0,0,0.06), 0 0 12px ${color}26`,
          borderColor: `${color}66`
        }
      }}
    >
      {/* Icône + libellé */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, mb: 1.5 }}>
        <Box
          sx={{
            width: 38,
            height: 38,
            flexShrink: 0,
            borderRadius: '11px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: `${color}1f`,
            color
          }}
        >
          {icon}
        </Box>
        <Typography
          sx={{
            fontWeight: 700,
            letterSpacing: '0.6px',
            textTransform: 'uppercase',
            fontSize: '0.72rem',
            color: 'text.secondary'
          }}
        >
          {label}
        </Typography>
      </Box>

      {/* Valeur principale */}
      {!loading ? (
        <Typography
          sx={{
            fontWeight: 800,
            color: 'text.primary',
            fontSize: '1.85rem',
            lineHeight: 1.1,
            letterSpacing: '-0.5px'
          }}
        >
          <CountUp
            end={Number(value) || 0}
            separator=" "
            decimals={0}
            duration={1.1}
            suffix={isCurrency ? ' €' : ''}
          />
        </Typography>
      ) : (
        <Skeleton variant="text" width={90} height={40} animation="wave" />
      )}

      {/* Sous-titre */}
      {!loading ? (
        subtitle ? (
          <Typography sx={{ mt: 0.5, fontSize: '0.78rem', color: 'text.secondary', fontWeight: 500 }}>
            {subtitle}
          </Typography>
        ) : (
          <Box sx={{ height: 18, mt: 0.5 }} />
        )
      ) : (
        <Skeleton variant="text" width={70} height={18} animation="wave" sx={{ mt: 0.5 }} />
      )}
    </Card>
  );
};

const DashboardKpiRow = ({ selectedYear }) => {
  const mounted = useMounted();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({});

  const getKpis = useCallback(async () => {
    try {
      const result = await dashboardApi.getKpis(localStorage.getItem('accessToken'), selectedYear);
      if (mounted.current && result) {
        setData(result);
      }
    } catch (err) {
      console.error('Erreur chargement KPIs dashboard:', err);
    } finally {
      if (mounted.current) {
        setLoading(false);
      }
    }
  }, [mounted, selectedYear]);

  useEffect(() => {
    getKpis();
    const interval = setInterval(getKpis, 60000);
    return () => clearInterval(interval);
  }, [getKpis]);

  const cards = [
    {
      key: 'apprenants',
      label: 'Apprenants',
      value: data.apprenants,
      color: '#3b82f6',
      icon: <PeopleAltRounded sx={{ fontSize: '1.3rem' }} />
    },
    {
      key: 'prospects',
      label: 'Prospects',
      value: data.prospects,
      color: '#8b5cf6',
      icon: <PersonSearchRounded sx={{ fontSize: '1.3rem' }} />
    },
    {
      key: 'opp',
      label: 'Opp. ouvertes',
      value: data.openOpportunities,
      color: '#f97316',
      icon: <TrackChangesRounded sx={{ fontSize: '1.3rem' }} />,
      subtitle: formatPipeline(data.pipelineCA)
    },
    {
      key: 'signedCA',
      label: 'CA signé',
      value: data.signedCA,
      color: '#10b981',
      icon: <PaidRounded sx={{ fontSize: '1.3rem' }} />,
      isCurrency: true
    },
    {
      key: 'campaigns',
      label: 'Campagnes',
      value: data.campaigns,
      color: '#f59e0b',
      icon: <CampaignRounded sx={{ fontSize: '1.3rem' }} />
    },
    {
      key: 'signatures',
      label: 'Signatures',
      value: data.pendingSignatures,
      color: '#ec4899',
      icon: <BorderColorRounded sx={{ fontSize: '1.3rem' }} />,
      subtitle: 'en attente'
    }
  ];

  return (
    <Grid container spacing={2}>
      {cards.map((card) => (
        <Grid item key={card.key} xs={12} sm={6} md={4} lg={2}>
          <KpiCard
            loading={loading}
            label={card.label}
            value={card.value}
            color={card.color}
            icon={card.icon}
            subtitle={card.subtitle}
            isCurrency={card.isCurrency}
          />
        </Grid>
      ))}
    </Grid>
  );
};

export default DashboardKpiRow;
